import Reveal from "@/components/Reveal";

const FEATURED_PANEL = {
  eyebrow: "Featured panel",
  venue: "ISPOR 2026 · Philadelphia",
  title:
    "Real-World Patient Voice at Scale: Can Social Media Listening Inform US Payer and Access Decisions?",
  body:
    "A cross-disciplinary panel exploring the credibility, integration, ethical guardrails, and payer relevance of social media listening as a complementary patient-experience evidence stream.",
  venueLine: "Pennsylvania Convention Center · Philadelphia",
};

const VENUES = [
  {
    venue: "ISPOR 2026",
    location: "Philadelphia, USA",
    date: "17 – 20 May 2026",
    kind: "Accepted Posters",
    works: [
      {
        title:
          "Social Media Conversations on GLP-1 Therapies for Obesity: Real-World Experiences and Treatment Narratives",
      },
      {
        title:
          "Social Media Sentiment Toward Anti-Amyloid Therapies in Early Alzheimer's Disease: Implications for Real-World Evidence and Decision-Making",
      },
      {
        title:
          "An Automated Social Media Listening Analysis of Conversations on CGRP Preventive Therapies in Migraine",
      },
      {
        title:
          "Social Media Conversations on Immune Checkpoint Inhibitors: Treatment Experiences, Safety, Access, and Patient Perspectives",
      },
      {
        title:
          "Real-World Online Conversations on MASH/NASH: A One-Year Social Media Listening Analysis",
      },
    ],
  },
  {
    venue: "AMCP 2026",
    location: "Nashville, USA",
    date: "13 – 16 April 2026",
    kind: "Accepted Poster",
    works: [
      {
        title:
          "Real-World Access and Affordability Barriers in Anal Cancer: Insights From a 5-Year Global Social Media Listening Study",
      },
    ],
  },
  {
    venue: "World EPA Congress 2026",
    location: "Amsterdam, Europe",
    date: "3 – 4 March 2026",
    kind: "Accepted Poster",
    works: [
      {
        title:
          "Social Media Discussions of Probiotic Use in Irritable Bowel Syndrome (IBS): Real-World Use Patterns and Patient-Reported Experiences",
      },
    ],
  },
  {
    venue: "Journal of Clinical Oncology",
    location: "Peer-reviewed publication",
    date: "2026",
    kind: "Publication",
    works: [
      {
        title:
          "Sociocultural and educational determinants of delayed diagnosis in anal cancer: Insights from a 5-year global social media analysis.",
        citation: "J Clin Oncol 44, 7 (2026)",
      },
    ],
  },
];

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function EngagementsSection() {
  return (
    <section
      className="bg-ink-2 relative overflow-hidden"
      data-testid="engagements-section"
    >
      <div className="container-edge py-24 lg:py-32">
        {/* Featured Panel */}
        <Reveal>
          <div className="card-dark p-8 lg:p-12 relative overflow-hidden" data-testid="featured-panel">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-rust">
                {FEATURED_PANEL.eyebrow}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-on-dark">
                {FEATURED_PANEL.venue}
              </span>
            </div>
            <h3 className="font-serif text-2xl md:text-4xl text-parchment-bright leading-snug max-w-4xl">
              {FEATURED_PANEL.title}
            </h3>
            <p className="mt-6 text-parchment/75 leading-relaxed max-w-3xl">
              {FEATURED_PANEL.body}
            </p>
            <div className="mt-7 flex items-center gap-3 text-[13px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-on-dark">
                Venue
              </span>
              <span className="text-parchment/85">{FEATURED_PANEL.venueLine}</span>
            </div>
          </div>
        </Reveal>

        {/* Published Work */}
        <Reveal className="mt-20">
          <div className="eyebrow">Published work</div>
          <h3 className="font-serif text-3xl md:text-4xl lg:text-[3rem] text-parchment-bright mt-3 max-w-3xl leading-[1.1] tracking-tight">
            Patient-voice evidence across therapy areas, geographies, and decision forums.
          </h3>
        </Reveal>

        <div className="mt-12 space-y-14">
          {VENUES.map((v) => (
            <div
              key={v.venue}
              className="border border-white/[0.08] bg-ink-1"
              data-testid={`venue-block-${slugify(v.venue)}`}
            >
              {/* Venue header */}
              <Reveal>
                <div className="px-7 lg:px-9 pt-7 lg:pt-8 pb-5 border-b border-white/[0.08]">
                  <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                    <h4 className="font-serif text-[1.65rem] md:text-[1.85rem] text-parchment-bright leading-tight">
                      {v.venue}
                    </h4>
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-rust">
                      {v.kind}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[11.5px] uppercase tracking-[0.18em] text-parchment/55">
                    <span>{v.date}</span>
                    <span className="text-parchment/30">·</span>
                    <span>{v.location}</span>
                  </div>
                </div>
              </Reveal>

              {/* Works list */}
              <ul>
                {v.works.map((w, i) => (
                  <Reveal
                    key={w.title}
                    delay={i * 60}
                    className="border-b border-white/[0.06] last:border-b-0"
                  >
                    <li
                      className="px-7 lg:px-9 py-7 lg:py-8 grid grid-cols-12 gap-6 items-start hover:bg-white/[0.015] transition-colors"
                      data-testid={`work-${slugify(v.venue)}-${i + 1}`}
                    >
                      <div className="col-span-12 md:col-span-1 font-mono text-[11px] uppercase tracking-[0.22em] text-rust pt-1">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className={`col-span-12 ${w.download ? "md:col-span-8" : "md:col-span-11"}`}>
                        <p className="font-serif text-[1.2rem] md:text-[1.4rem] text-parchment-bright leading-snug">
                          {w.title}
                        </p>
                        {w.citation && (
                          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-parchment/55">
                            {w.citation}
                          </p>
                        )}
                      </div>
                      {w.download && (
                        <div className="col-span-12 md:col-span-3 md:text-right">
                          <a
                            href={w.download}
                            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-rust hover:text-parchment-bright transition-colors"
                            data-testid={`download-${slugify(v.venue)}-${i + 1}`}
                          >
                            <span>Download</span>
                            <span aria-hidden="true" className="text-[13px] leading-none">↓</span>
                          </a>
                        </div>
                      )}
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Reveal className="mt-12">
          <p className="text-sm text-parchment/55 max-w-3xl leading-relaxed italic">
            Selected abstracts, posters, and panel details are available during briefing conversations where relevant to the asset, indication, or evidence question.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
